import {
    MediaPackProgram,
    MediaProgram,
    WorkflowWorkflowInfo,
    WorkflowWorkflowState,
    WorkflowWorkflowType,
} from '@calm/evexia-web-sdk';
import { logger } from '@rhg/libs/logger';
import { routes } from '../routes';

export enum ProgramState {
    NotJoined = 'not-joined',
    InProgress = 'in-progress',
    Completed = 'completed',
    Abandoned = 'abandoned',
    Unspecified = '',
}

export const workflowStateToProgramStateMap = {
    [WorkflowWorkflowState.Created]: ProgramState.InProgress,
    [WorkflowWorkflowState.InProgress]: ProgramState.InProgress,
    [WorkflowWorkflowState.Completed]: ProgramState.Completed,
    [WorkflowWorkflowState.Abandoned]: ProgramState.Abandoned,
};

export function getClinicalProgramState(workflow?: WorkflowWorkflowInfo) {
    if (!workflow) {
        return ProgramState.Unspecified;
    }

    if (!workflow?.workflowInstance) {
        return ProgramState.NotJoined;
    }

    return (
        workflowStateToProgramStateMap[
            workflow?.workflowInstance?.state ?? ''
        ] ?? ProgramState.Unspecified
    );
}

export function getProgramType(
    program?: MediaProgram,
    workflow?: WorkflowWorkflowInfo
) {
    return workflow?.workflowDefn.type ?? program?.isClinical
        ? WorkflowWorkflowType.ClinicalProgram
        : undefined;
}

export function getProgramSessionDurationRange(program: MediaProgram) {
    return program.guides?.reduce(
        ({ min, max }, guide) => ({
            min: Math.min(min, guide?.totalDuration ?? Infinity),
            max: Math.max(max, guide?.totalDuration ?? 0),
        }),
        { min: Infinity, max: 0 }
    );
}

/** will move completed programs to the back if willSortCompletion is true */
export const sortPrograms =
    (willSortCompletion = true) =>
    (a?: MediaProgram, b?: MediaProgram) => {
        const completionSort = willSortCompletion
            ? (a?.completed ? 1 : 0) - (b?.completed ? 1 : 0)
            : 0;
        return completionSort
            ? completionSort
            : a?.title?.localeCompare(b?.title ?? '') ?? 0;
    };

export function getIsJoined(workflow?: WorkflowWorkflowInfo) {
    return getClinicalProgramState(workflow) === ProgramState.InProgress;
}

/**
 * Retrieves the `pack_class` metadata from the given program.
 *
 * @param {MediaProgram | MediaPackProgram} [program] - The program object, which may contain metadata.
 * @returns {string | undefined} The `pack_class` value if it exists and is a string; otherwise, `undefined`.
 */
export function getProgramPackClassFromMetadata(
    program?: MediaProgram | MediaPackProgram
): string | undefined {
    const packClass = program?.metadata?.pack_class;
    const result = typeof packClass === 'string' ? packClass : undefined;
    if (program?.isClinical && !result && process.env.NODE_ENV !== 'test') {
        const programId =
            (program as MediaProgram).id ??
            (program as MediaPackProgram).programId;
        logger.error(
            'Clinical Program Misconfigured',
            { type: 'CONTENT', programId },
            new Error('Clinical Program Misconfigured')
        );
    }
    return result;
}

/**
 * Creates the program details route for the provided program.
 * Prefers the pack_class in the program metadata, when present, for efficient retrieval of associated workflows and recommendations
 *
 * @param {MediaProgram} [program] - The program object, which may contain metadata.
 * @param {packClass} [string] - The pack class that the program was sent in. Used as a fallback if the program has no metadata.
 * @param {showOverview} [string] - If present, forces the program page to show the overview tab, not the schedule tab
 * @returns {string | undefined} The route (for use in e.g. href or router.push) for the program detail page
 */
export function getProgramRoute(
    program: MediaProgram,
    packClass: string,
    showOverview?: string
): string;
export function getProgramRoute(
    program?: MediaProgram,
    packClass?: string,
    showOverview?: string
): string | undefined;
export function getProgramRoute(
    program?: MediaProgram,
    packClass?: string,
    showOverview = ''
): string | undefined {
    if (!program) {
        return undefined;
    }
    const packClassForPush =
        getProgramPackClassFromMetadata(program) ?? packClass;
    if (!packClassForPush) {
        return undefined;
    }
    return routes.programDetails(packClassForPush, program.id, showOverview);
}

/**
 * Many V1 endpoints (e.g., GET bulk programs) do not work when seeing some pack classes
 * (seems to be: "non-database-backed" pack classes, like search right now)
 *
 * They also couple packClass with data where there should be no coupling
 * (e.g., packClass is saved along with a user favorite, so if the same
 * guide is returned in another pack class elsewhere, it won't show up as favorited)
 *
 * So: for now, remap "non-database-backed" pack classes to a "known good" pack class.
 * Make sure to do this everywhere the program or guide is coupled with the pack class.
 * This is a temporary workaround until we can fix the underlying issue, ideally in Media v2
 */
const packClassBlocklist = new Set([
    'clinical-search-results',
    'meditations-search-results',
    'sleep_stories-search-results',
    'music-search-results',
    'soundscapes-search-results',
    'breathing_exercises-search-results',
    'daily_programs-search-results',
    'wisdom-search-results',
    'movement-search-results',
    'kids-search-results',
    'others-search-results',
    'ch-home-favorites',
    'ch-home-recents',
    'ch-home-todays-dailies',
    'ch-today-and-past-five-daily-calm',
]);
const knownGoodPackClass =
    process.env.NEXT_PUBLIC_DISCOVER_PAGE_BASE_PACK || 'calm-health-discover';
export function getRemappedPackClass(packClass: string) {
    return packClassBlocklist.has(packClass) ? knownGoodPackClass : packClass;
}
